<template>
  <div class="customer-display-page">
    <div class="customer-display-orb orb-one"></div>
    <div class="customer-display-orb orb-two"></div>

    <div
      ref="displayViewport"
      class="customer-display-viewport"
      :style="{ '--display-scale': displayScale.toString() }"
    >
      <header class="customer-display-header">
        <div class="brand-wrap">
          <div class="brand-mark">
            <img
              v-if="brandLogo"
              :src="brandLogo"
              :alt="`${displayState.branding.site_name} logo`"
            />
            <span v-else>{{ brandInitial }}</span>
          </div>

          <div class="brand-copy">
            <span class="brand-eyebrow">{{ $t("pos.customer_screen") }}</span>
            <h1>{{ displayState.branding.site_name }}</h1>
            <p>{{ heroMessage }}</p>
          </div>
        </div>

        <div class="header-meta">
          <div class="meta-card">
            <span>{{ $t("pos.now_serving") }}</span>
            <strong>{{ liveTime }}</strong>
            <small>{{ liveDate }}</small>
          </div>
          <div class="meta-card status-card">
            <span>{{ $t("pos.screen_mode") }}</span>
            <strong>{{ statusLabel }}</strong>
            <small>{{ statusCaption }}</small>
          </div>
        </div>
      </header>

      <main class="customer-display-main">
        <section class="transaction-stage">
          <div class="receipt-shell">
            <div class="receipt-head">
              <div>
                <span class="section-kicker">{{ invoiceKicker }}</span>
                <h2>{{ hasActiveInvoice ? activeReference : $t("pos.waiting_for_next_invoice") }}</h2>
              </div>
              <span class="receipt-status">{{ invoiceStatus }}</span>
            </div>

            <template v-if="hasActiveInvoice">
              <div class="receipt-customer">
                <div class="receipt-customer-card">
                  <span>{{ $t("pos.customer") }}</span>
                  <strong>{{ activeCustomerName }}</strong>
                </div>
                <div class="receipt-customer-card">
                  <span>{{ $t("pos.counter") }}</span>
                  <strong>{{ activeWarehouseName }}</strong>
                </div>
                <div class="receipt-customer-card" v-if="isCompletedMode">
                  <span>{{ $t("pos.sale_time") }}</span>
                  <strong>{{ displayState.bill?.date || "-" }}</strong>
                </div>
              </div>

              <div class="receipt-items">
                <div class="receipt-items-head">
                  <span>{{ $t("pos.items") }}</span>
                  <span>{{ $t("pos.item_count_total", { count: activeItemsCount }) }}</span>
                </div>

                <div class="receipt-row receipt-row-head">
                  <span>{{ $t("pos.product") }}</span>
                  <span>{{ $t("pos.qty") }}</span>
                  <span>{{ $t("pos.total") }}</span>
                </div>

                <div
                  v-for="item in activeItems"
                  :key="item.id"
                  class="receipt-row"
                >
                  <div class="receipt-product">
                    <strong>{{ item.name }}</strong>
                    <small>{{ formatMoney(item.unit_price) }} each</small>
                    <small
                      v-if="getLineSelectionLabel(item)"
                      class="receipt-variant"
                    >
                      {{ getLineSelectionLabel(item) }}
                    </small>
                    <small
                      v-if="item.discount_amount > 0"
                      class="receipt-promo"
                    >
                      {{ getLinePromotionLabel(item) }}
                    </small>
                  </div>
                  <span>{{ item.quantity }}</span>
                  <strong>{{ formatMoney(item.total) }}</strong>
                </div>
              </div>

              <div class="receipt-totals">
                <div class="total-row">
                  <span>{{ $t("pos.subtotal") }}</span>
                  <strong>{{ formatMoney(activeBaseSubtotal) }}</strong>
                </div>
                <div
                  v-if="activeItemDiscountTotal > 0"
                  class="total-row is-negative"
                >
                  <span>{{ $t("pos.item_promotions") }}</span>
                  <strong>-{{ formatMoney(activeItemDiscountTotal) }}</strong>
                </div>
                <div v-if="activeDiscount > 0" class="total-row is-negative">
                  <span>{{ activePromotion ? activePromotion.name : $t("pos.order_discount") }}</span>
                  <strong>-{{ formatMoney(activeDiscount) }}</strong>
                </div>
                <div v-if="activeCouponDiscount > 0" class="total-row is-negative">
                  <span>{{ $t("pos.coupon") }}</span>
                  <strong>-{{ formatMoney(activeCouponDiscount) }}</strong>
                </div>
                <div v-if="activeTax > 0" class="total-row">
                  <span>{{ $t("pos.tax") }}</span>
                  <strong>{{ formatMoney(activeTax) }}</strong>
                </div>
                <div v-if="activeShipping > 0" class="total-row">
                  <span>{{ $t("pos.shipping") }}</span>
                  <strong>{{ formatMoney(activeShipping) }}</strong>
                </div>
                <div class="total-row is-grand">
                  <span>{{ $t("pos.total_due") }}</span>
                  <strong>{{ formatMoney(activeGrandTotal) }}</strong>
                </div>
                <div v-if="isCompletedMode" class="total-row">
                  <span>{{ $t("pos.amount_received") }}</span>
                  <strong>{{ formatMoney(activeAmountReceived) }}</strong>
                </div>
                <div v-if="isCompletedMode" class="total-row">
                  <span>{{ activeDue > 0 ? $t("pos.balance_due") : $t("pos.change_return") }}</span>
                  <strong>{{ formatMoney(activeDue > 0 ? activeDue : activeChange) }}</strong>
                </div>
              </div>
            </template>

            <div v-else class="receipt-empty-state">
              <p>
                {{ $t("pos.customer_display_empty_message") }}
              </p>

              <div class="receipt-empty-grid">
                <div class="receipt-customer-card">
                  <span>{{ $t("pos.status") }}</span>
                  <strong>{{ $t("pos.available_for_next_invoice") }}</strong>
                </div>
                <div class="receipt-customer-card">
                  <span>{{ $t("pos.counter") }}</span>
                  <strong>{{ displayState.branding.site_name }}</strong>
                </div>
              </div>

              <div class="receipt-totals is-empty">
                <div class="total-row">
                  <span>{{ $t("pos.subtotal") }}</span>
                  <strong>{{ formatMoney(0) }}</strong>
                </div>
                <div class="total-row">
                  <span>{{ $t("pos.promotions") }}</span>
                  <strong>{{ formatMoney(0) }}</strong>
                </div>
                <div class="total-row is-grand">
                  <span>{{ $t("pos.total_due") }}</span>
                  <strong>{{ formatMoney(0) }}</strong>
                </div>
              </div>
            </div>
          </div>

          <aside class="experience-shell">
            <div v-if="showQrPanel" class="payment-shell">
              <span class="section-kicker">{{ $t("pos.scan_to_pay") }}</span>
              <h3>{{ activePaymentMethodLabel }}</h3>
              <p>{{ heroMessage }}</p>

              <div class="qr-card">
                <QRCodeGenerator :value="qrValue" :size="qrCodeSize" />
              </div>

              <div class="payment-amount">
                <span>{{ $t("pos.amount") }}</span>
                <strong>{{ formatMoney(activeGrandTotal) }}</strong>
              </div>

              <div class="payment-note">
                {{ displayState.branding.note_to_customer }}
              </div>
            </div>

            <div v-else class="promo-shell">
              <div v-if="activePromotion" class="promo-live-wrap">
                <span class="section-kicker">{{ $t("pos.live_promotion") }}</span>
                <h3>{{ activePromotion.name }}</h3>
                <p>{{ activePromotionDescription }}</p>

                <div class="promo-banner promo-banner-live">
                  <strong>{{ activePromotionValueLabel }}</strong>
                  <span>{{ activePromotionTag }}</span>
                </div>

                <div class="promo-live-meta">
                  <article class="promo-live-card">
                    <span>{{ $t("pos.applies_to") }}</span>
                    <strong>{{ activePromotion.customer_name || $t("pos.all_walk_in_and_selected_customers") }}</strong>
                  </article>
                  <article class="promo-live-card">
                    <span>{{ $t("pos.schedule") }}</span>
                    <strong>{{ activePromotionSchedule }}</strong>
                  </article>
                  <article class="promo-live-card">
                    <span>{{ $t("pos.estimated_saving") }}</span>
                    <strong>{{ formatMoney(activePromotion.applied_amount || 0) }}</strong>
                  </article>
                </div>
              </div>

              <div v-else class="promo-image-wrap">
                <div class="promo-image-stage">
                  <img
                    v-if="activePosterImage"
                    :src="activePosterImage"
                    :alt="activePosterCaption"
                  />
                  <div v-else class="promo-image-fallback">
                    {{ displayState.branding.site_name }}
                  </div>
                  <div class="promo-caption-panel">
                    <span class="section-kicker">{{ $t("pos.house_promotions") }}</span>
                    <h3>{{ activePosterCaption }}</h3>
                  </div>
                </div>

                <div class="promo-list">
                  <article
                    v-for="poster in posterCards"
                    :key="`${poster.image || posterCaption(poster)}-aside`"
                    class="promo-list-card"
                  >
                    <img
                      v-if="posterImageUrl(poster)"
                      :src="posterImageUrl(poster)"
                      :alt="posterCaption(poster)"
                    />
                    <strong>{{ posterCaption(poster) }}</strong>
                  </article>
                </div>
              </div>
            </div>
          </aside>
        </section>
      </main>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import QRCodeGenerator from "@/components/Common/QRCodeGenerator.vue";
import {
  buildAssetUrl,
} from "@/utils/settings";
import { DEFAULT_CUSTOMER_DISPLAY_POSTERS } from "@/utils/customerDisplayPosters";
import {
  POS_CUSTOMER_DISPLAY_EVENT,
  POS_CUSTOMER_DISPLAY_STORAGE_KEY,
  buildCustomerPaymentQrValue,
  createDefaultCustomerDisplayState,
  isQrPaymentMethod,
  readCustomerDisplayState,
  type CustomerDisplayLineItem,
  type CustomerDisplayPromotion,
  type CustomerDisplayState,
} from "@/utils/posCustomerDisplay";
import {
  formatPromotionDays,
  formatPromotionValue,
} from "@/utils/promotion";
import type { CustomerDisplayPoster } from "@/services/setting";

const POSTER_TONES = ["tone-amber", "tone-cream", "tone-teal"] as const;

interface PosterCard extends CustomerDisplayPoster {
  tone: string;
}

export default defineComponent({
  name: "PosCustomerDisplayPage",
  components: {
    QRCodeGenerator,
  },
  setup() {
    const { t, locale } = useI18n();
    const displayState = ref<CustomerDisplayState>(
      createDefaultCustomerDisplayState()
    );
    const displayViewport = ref<HTMLElement | null>(null);
    const displayScale = ref(1);
    const viewportHeight = ref(
      typeof window !== "undefined" ? window.innerHeight : 1080
    );
    const now = ref(new Date());
    const activePosterIndex = ref(0);

    let clockTimer: number | null = null;
    let posterTimer: number | null = null;
    let scaleFrame: number | null = null;
    let previousBodyOverflow = "";
    let previousHtmlOverflow = "";
    let restoreDarkTheme = false;
    const activeLocale = computed(() =>
      String(locale.value || "en").toLowerCase().startsWith("km")
        ? "km-KH"
        : "en-US"
    );

    const applyDisplayState = (nextState?: CustomerDisplayState) => {
      displayState.value = nextState || readCustomerDisplayState();
      scheduleDisplayScale();
    };

    const handleStorageUpdate = (event: StorageEvent) => {
      if (
        event.key &&
        event.key !== POS_CUSTOMER_DISPLAY_STORAGE_KEY
      ) {
        return;
      }

      applyDisplayState();
    };

    const handleCustomUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<CustomerDisplayState>;
      applyDisplayState(customEvent.detail);
    };

    const updateDisplayScale = () => {
      if (!displayViewport.value) {
        return;
      }

      displayScale.value = 1;

      if (scaleFrame) {
        window.cancelAnimationFrame(scaleFrame);
      }

      scaleFrame = window.requestAnimationFrame(() => {
        const viewport = displayViewport.value;
        if (!viewport) {
          return;
        }

        const contentHeight = viewport.scrollHeight;
        const contentWidth = viewport.scrollWidth;
        const availableHeight = window.innerHeight;
        const availableWidth = window.innerWidth;
        const verticalScale = availableHeight / Math.max(contentHeight, 1);
        const horizontalScale = availableWidth / Math.max(contentWidth, 1);
        const nextScale = Math.min(1, verticalScale, horizontalScale);

        displayScale.value =
          Number.isFinite(nextScale) && nextScale > 0 ? nextScale : 1;
      });
    };

    const scheduleDisplayScale = () => {
      nextTick(() => {
        updateDisplayScale();
      });
    };

    const handleViewportResize = () => {
      viewportHeight.value = window.innerHeight;
      scheduleDisplayScale();
    };

    const brandLogo = computed(() =>
      buildAssetUrl(displayState.value.branding.logo || "")
    );

    const brandInitial = computed(() =>
      String(displayState.value.branding.site_name || "C")
        .trim()
        .charAt(0)
        .toUpperCase()
    );

    const isIdleMode = computed(() => displayState.value.mode === "idle");
    const isCompletedMode = computed(
      () => displayState.value.mode === "completed" && !!displayState.value.bill
    );

    const activeItems = computed<CustomerDisplayLineItem[]>(() =>
      isCompletedMode.value
        ? displayState.value.bill?.items || []
        : displayState.value.order?.items || []
    );

    const activeItemsCount = computed(() =>
      activeItems.value.reduce(
        (sum, item) => sum + Number(item.quantity || 0),
        0
      )
    );

    const hasActiveInvoice = computed(
      () => activeItems.value.length > 0 || isCompletedMode.value
    );

    const activeReference = computed(() =>
      isCompletedMode.value
        ? displayState.value.bill?.reference_no || "-"
        : displayState.value.order?.reference_no || t("pos.current_order")
    );

    const activeCustomerName = computed(() =>
      isCompletedMode.value
        ? displayState.value.bill?.customer_name || t("pos.walk_in_customer")
        : displayState.value.order?.customer_name || t("pos.walk_in_customer")
    );

    const activeWarehouseName = computed(() =>
      isCompletedMode.value
        ? displayState.value.bill?.warehouse_name || t("pos.main_counter")
        : displayState.value.order?.warehouse_name || t("pos.main_counter")
    );

    const activePaymentMethod = computed(() =>
      isCompletedMode.value
        ? displayState.value.bill?.paid_by || ""
        : displayState.value.order?.payment_method || ""
    );

    const activePaymentMethodLabel = computed(() => {
      const normalized = String(activePaymentMethod.value || "")
        .replace(/[\s-]+/g, "_")
        .toLowerCase();

      const keyMap: Record<string, string> = {
        cash: "payment_cash",
        card: "payment_card",
        khqr: "payment_khqr",
        aba: "payment_aba",
        aclida: "payment_aclida",
        wing: "payment_wing",
        gift_card: "payment_gift_card",
        bank: "payment_bank",
      };

      if (normalized && keyMap[normalized]) {
        return t(`pos.${keyMap[normalized]}`);
      }

      if (normalized) {
        return normalized.replace(/_/g, " ").toUpperCase();
      }

      return isCompletedMode.value
        ? t("pos.payment_cash")
        : t("pos.pending_payment");
    });

    const activeSubtotal = computed(() =>
      isCompletedMode.value
        ? Number(displayState.value.bill?.total || 0)
        : Number(displayState.value.order?.subtotal || 0)
    );

    const activeBaseSubtotal = computed(() =>
      activeItems.value.reduce(
        (sum, item) =>
          sum +
          Number(
            item.base_total ||
              Number(item.unit_price || 0) * Number(item.quantity || 0)
          ),
        0
      )
    );

    const activeItemDiscountTotal = computed(() =>
      activeItems.value.reduce(
        (sum, item) => sum + Number(item.discount_amount || 0),
        0
      )
    );

    const activeDiscount = computed(() =>
      isCompletedMode.value
        ? Number(displayState.value.bill?.discount || 0)
        : Number(displayState.value.order?.discount || 0)
    );

    const activeCouponDiscount = computed(() =>
      isCompletedMode.value
        ? Number(displayState.value.bill?.coupon_discount || 0)
        : Number(displayState.value.order?.coupon_discount || 0)
    );

    const activeTax = computed(() =>
      isCompletedMode.value
        ? Number(displayState.value.bill?.tax || 0)
        : Number(displayState.value.order?.tax || 0)
    );

    const activeShipping = computed(() =>
      isCompletedMode.value
        ? Number(displayState.value.bill?.shipping || 0)
        : Number(displayState.value.order?.shipping || 0)
    );

    const activeGrandTotal = computed(() =>
      isCompletedMode.value
        ? Number(displayState.value.bill?.grand_total || 0)
        : Number(displayState.value.order?.grand_total || 0)
    );

    const activePromotion = computed<CustomerDisplayPromotion | null>(() =>
      isCompletedMode.value
        ? displayState.value.bill?.promotion || null
        : displayState.value.order?.promotion || null
    );

    const activeAmountReceived = computed(() =>
      isCompletedMode.value
        ? Number(displayState.value.bill?.cash_received || 0)
        : Number(displayState.value.order?.amount_received || 0)
    );

    const activeChange = computed(() =>
      isCompletedMode.value
        ? Number(displayState.value.bill?.change_amount || 0)
        : Number(displayState.value.order?.change_amount || 0)
    );

    const activeDue = computed(() =>
      isCompletedMode.value
        ? Number(displayState.value.bill?.due || 0)
        : Math.max(0, activeGrandTotal.value - activeAmountReceived.value)
    );

    const showQrPanel = computed(() =>
      !isIdleMode.value && isQrPaymentMethod(activePaymentMethod.value)
    );

    const qrValue = computed(() =>
      buildCustomerPaymentQrValue({
        siteName: displayState.value.branding.site_name,
        referenceNo: activeReference.value,
        total: activeGrandTotal.value,
        paymentMethod: activePaymentMethod.value,
        customerName: activeCustomerName.value,
      })
    );

    const qrCodeSize = computed(() => {
      if (viewportHeight.value <= 760) return 160;
      if (viewportHeight.value <= 900) return 180;
      return 220;
    });

    const posterCardsPool = computed<PosterCard[]>(() => {
      const source =
        displayState.value.branding.posters?.length
          ? displayState.value.branding.posters
          : DEFAULT_CUSTOMER_DISPLAY_POSTERS;

      return source.map((poster, index) => ({
        ...poster,
        tone: POSTER_TONES[index % POSTER_TONES.length],
      }));
    });

    const activePoster = computed(
      () =>
        posterCardsPool.value[
          activePosterIndex.value % posterCardsPool.value.length
        ] || posterCardsPool.value[0]
    );

    const posterCaption = (poster?: CustomerDisplayPoster | null) =>
      String(poster?.caption || poster?.title || poster?.text || "").trim();

    const posterImageUrl = (poster?: CustomerDisplayPoster | null) =>
      buildAssetUrl(poster?.image || "");

    const activePosterCaption = computed(() =>
      posterCaption(activePoster.value)
    );

    const activePosterImage = computed(() =>
      posterImageUrl(activePoster.value)
    );

    const activePromotionValueLabel = computed(() =>
      activePromotion.value ? formatPromotionValue(activePromotion.value) : ""
    );

    const activePromotionDescription = computed(() => {
      if (!activePromotion.value) return "";

      return (
        activePromotion.value.description ||
        t("pos.promotion_active_checkout_message")
      );
    });

    const activePromotionTag = computed(() => {
      if (!activePromotion.value) return "";

      return activePromotion.value.customer_name
        ? t("pos.promotion_for_customer_tag", {
            customer: activePromotion.value.customer_name,
          })
        : t("pos.available_for_all_customers");
    });

    const activePromotionSchedule = computed(() => {
      if (!activePromotion.value) return "";
      return formatPromotionDays(activePromotion.value.valid_day);
    });

    const posterCards = computed(() => {
      if (!posterCardsPool.value.length) {
        return [];
      }

      return posterCardsPool.value
        .filter((_, index) => index !== activePosterIndex.value)
        .concat(
          posterCardsPool.value[
            activePosterIndex.value % posterCardsPool.value.length
          ]
        )
        .slice(0, 3);
    });

    const statusLabel = computed(() => {
      if (isIdleMode.value) return t("pos.customer_display_status_welcome");
      if (showQrPanel.value) return t("pos.customer_display_status_qr_payment");
      if (isCompletedMode.value) return t("pos.customer_display_status_receipt_ready");
      return t("pos.customer_display_status_live_order");
    });

    const statusCaption = computed(() => {
      if (isIdleMode.value) return t("pos.customer_display_caption_welcome");
      if (showQrPanel.value) return t("pos.customer_display_caption_qr_payment");
      if (isCompletedMode.value) return t("pos.customer_display_caption_receipt_ready");
      return t("pos.customer_display_caption_live_order");
    });

    const heroMessage = computed(() => {
      if (isIdleMode.value) {
        return t("pos.customer_display_idle_message");
      }

      if (showQrPanel.value) {
        return t("pos.customer_display_qr_message");
      }

      if (isCompletedMode.value) {
        return t("pos.customer_display_completed_message");
      }

      return t("pos.customer_display_order_message");
    });

    const invoiceKicker = computed(() => {
      if (isCompletedMode.value) return t("pos.final_receipt");
      if (hasActiveInvoice.value) return t("pos.order_preview");
      return t("pos.invoice_ready_area");
    });

    const invoiceStatus = computed(() => {
      if (!hasActiveInvoice.value) return t("pos.available_for_next_invoice");
      return activePaymentMethodLabel.value;
    });

    const liveTime = computed(() =>
      new Intl.DateTimeFormat(activeLocale.value, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      }).format(now.value)
    );

    const liveDate = computed(() =>
      new Intl.DateTimeFormat(activeLocale.value, {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      }).format(now.value)
    );

    const formatMoney = (amount: number) => {
      const value = Number(amount || 0).toFixed(2);
      const symbol = displayState.value.branding.currency_symbol || "$";

      return symbol === "៛" ? `${value} ${symbol}` : `${symbol}${value}`;
    };

    const getLinePromotionLabel = (item: CustomerDisplayLineItem) => {
      const discountAmount = Number(item.discount_amount || 0);
      if (discountAmount <= 0) return "";

      if (
        item.discount_type === "percentage" &&
        Number(item.discount_value || 0) > 0
      ) {
        return t("pos.promotion_percentage_saved", {
          value: Number(item.discount_value || 0),
          amount: formatMoney(discountAmount),
        });
      }

      return t("pos.promotion_saved", {
        amount: formatMoney(discountAmount),
      });
    };

    const getLineSelectionLabel = (item: CustomerDisplayLineItem) => {
      const selections: string[] = [];

      const sizeName = String(item.size_name || "").trim();
      const sugarName = String(item.sugar_name || "").trim();

      if (sizeName) {
        selections.push(`${t("pos.size")}: ${sizeName}`);
      }

      if (sugarName) {
        selections.push(`${t("pos.sugar")}: ${sugarName}`);
      }

      return selections.join(" • ");
    };

    watch(displayState, scheduleDisplayScale, { deep: true });
    watch(activePosterIndex, scheduleDisplayScale);

    onMounted(() => {
      previousBodyOverflow = document.body.style.overflow;
      previousHtmlOverflow = document.documentElement.style.overflow;
      restoreDarkTheme = document.body.classList.contains("theme-dark");

      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      document.body.classList.remove("theme-dark");

      applyDisplayState();
      window.addEventListener("storage", handleStorageUpdate);
      window.addEventListener(POS_CUSTOMER_DISPLAY_EVENT, handleCustomUpdate);
      window.addEventListener("resize", handleViewportResize, {
        passive: true,
      });

      clockTimer = window.setInterval(() => {
        now.value = new Date();
      }, 1000);

      posterTimer = window.setInterval(() => {
        if (posterCardsPool.value.length <= 1) {
          activePosterIndex.value = 0;
          return;
        }

        activePosterIndex.value =
          (activePosterIndex.value + 1) % posterCardsPool.value.length;
      }, 6000);

      scheduleDisplayScale();
    });

    onUnmounted(() => {
      window.removeEventListener("storage", handleStorageUpdate);
      window.removeEventListener(
        POS_CUSTOMER_DISPLAY_EVENT,
        handleCustomUpdate
      );
      window.removeEventListener("resize", handleViewportResize);

      if (clockTimer) {
        window.clearInterval(clockTimer);
      }

      if (posterTimer) {
        window.clearInterval(posterTimer);
      }

      if (scaleFrame) {
        window.cancelAnimationFrame(scaleFrame);
      }

      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;

      if (restoreDarkTheme) {
        document.body.classList.add("theme-dark");
      }
    });

    return {
      activeChange,
      activeCouponDiscount,
      activeCustomerName,
      activeDiscount,
      activeBaseSubtotal,
      activeDue,
      activeGrandTotal,
      activeItemDiscountTotal,
      activeItems,
      activeItemsCount,
      activePaymentMethodLabel,
      activePoster,
      activePosterCaption,
      activePosterImage,
      activePromotion,
      activePromotionDescription,
      activePromotionSchedule,
      activePromotionTag,
      activePromotionValueLabel,
      activeReference,
      activeShipping,
      activeSubtotal,
      activeTax,
      activeWarehouseName,
      brandInitial,
      brandLogo,
      displayScale,
      displayState,
      displayViewport,
      formatMoney,
      getLineSelectionLabel,
      getLinePromotionLabel,
      hasActiveInvoice,
      heroMessage,
      invoiceKicker,
      invoiceStatus,
      isCompletedMode,
      isIdleMode,
      liveDate,
      liveTime,
      posterCaption,
      posterCards,
      posterImageUrl,
      qrCodeSize,
      qrValue,
      showQrPanel,
      statusCaption,
      statusLabel,
      activeAmountReceived,
    };
  },
});
</script>

<style scoped lang="scss">
.customer-display-page {
  --coffee-ink: #2d1f16;
  --coffee-paper: #ffffff;
  --coffee-paper-soft: #fff8ef;
  --coffee-gold: #f6b756;
  --coffee-brown: #7c4a2b;
  --coffee-mint: #78d5c4;
  --coffee-line: rgba(124, 74, 43, 0.12);
  --coffee-muted: rgba(45, 31, 22, 0.68);
  --coffee-shadow: rgba(111, 73, 48, 0.12);

  position: relative;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(246, 183, 86, 0.2), transparent 28%),
    radial-gradient(circle at right center, rgba(120, 213, 196, 0.18), transparent 24%),
    linear-gradient(180deg, #fffdf9 0%, #fff7ef 52%, #f8efe4 100%);
  color: var(--coffee-ink);
  font-family: "Poppins", "Trebuchet MS", sans-serif;
}

.customer-display-viewport {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: clamp(14px, 1.8vh, 22px);
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  padding: clamp(16px, 2vh, 24px);
  transform: scale(var(--display-scale, 1));
  transform-origin: top center;
}

.customer-display-orb {
  position: absolute;
  border-radius: 999px;
  filter: blur(12px);
  opacity: 0.75;
  pointer-events: none;
}

.orb-one {
  top: -120px;
  right: -80px;
  width: 320px;
  height: 320px;
  background: rgba(246, 183, 86, 0.22);
}

.orb-two {
  left: -120px;
  bottom: -140px;
  width: 360px;
  height: 360px;
  background: rgba(120, 213, 196, 0.16);
}

.customer-display-header {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 18px;
}

.customer-display-main {
  flex: 1;
  min-height: 0;
  display: flex;
}

.brand-wrap {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.brand-mark {
  width: 70px;
  height: 70px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid var(--coffee-line);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 14px 28px var(--coffee-shadow);
  overflow: hidden;
  flex-shrink: 0;
}

.brand-mark img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.brand-mark span {
  font-size: 1.8rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: var(--coffee-brown);
}

.brand-copy {
  min-width: 0;
}

.brand-copy h1 {
  margin: 3px 0 4px;
  font-size: clamp(1.8rem, 2.4vw, 2.45rem);
  font-weight: 800;
  color: var(--coffee-ink);
}

.brand-copy p,
.promo-shell p,
.payment-shell p,
.receipt-empty-state p {
  margin: 0;
  color: var(--coffee-muted);
  font-size: clamp(0.88rem, 1.1vw, 0.98rem);
  line-height: 1.45;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.brand-copy p {
  max-width: 660px;
  -webkit-line-clamp: 2;
}

.promo-shell p,
.payment-shell p {
  -webkit-line-clamp: 3;
}

.receipt-empty-state p {
  max-width: 520px;
  -webkit-line-clamp: 3;
}

.brand-eyebrow,
.section-kicker {
  display: inline-flex;
  align-items: center;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 0.7rem;
  font-weight: 700;
  color: rgba(124, 74, 43, 0.82);
}

.header-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(170px, 1fr));
  gap: 12px;
  min-width: 340px;
}

.meta-card {
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid var(--coffee-line);
  box-shadow: 0 12px 24px rgba(111, 73, 48, 0.08);
}

.meta-card span,
.meta-card small {
  display: block;
  color: rgba(45, 31, 22, 0.58);
}

.meta-card strong {
  display: block;
  margin: 6px 0 2px;
  font-size: 1.15rem;
  color: var(--coffee-ink);
}

.status-card {
  background: linear-gradient(180deg, rgba(237, 252, 247, 0.92), rgba(248, 254, 252, 0.92));
}

.transaction-stage {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, 0.92fr);
  gap: 18px;
  align-items: stretch;
  flex: 1;
  min-height: 0;
}

.receipt-shell,
.experience-shell {
  min-height: 0;
  border-radius: 24px;
  border: 1px solid var(--coffee-line);
  box-shadow: 0 18px 36px var(--coffee-shadow);
}

.receipt-shell {
  display: flex;
  flex-direction: column;
  padding: 22px;
  background: rgba(255, 255, 255, 0.96);
  color: var(--coffee-ink);
}

.experience-shell {
  display: flex;
  min-height: 0;
  padding: 22px;
  background: rgba(255, 251, 245, 0.9);
}

.receipt-head,
.receipt-items-head,
.receipt-row,
.total-row,
.payment-amount,
.promo-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.receipt-head {
  margin-bottom: 16px;
}

.receipt-head h2 {
  margin: 6px 0 0;
  font-size: clamp(1.45rem, 2vw, 2rem);
  color: var(--coffee-ink);
}

.receipt-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 14px;
  border-radius: 999px;
  background: rgba(124, 74, 43, 0.08);
  color: var(--coffee-brown);
  font-weight: 700;
  font-size: 0.82rem;
  white-space: nowrap;
}

.receipt-customer {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.receipt-customer-card {
  padding: 14px 16px;
  border-radius: 18px;
  background: var(--coffee-paper-soft);
  border: 1px solid rgba(124, 74, 43, 0.08);
}

.receipt-customer-card span {
  display: block;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgba(45, 31, 22, 0.54);
}

.receipt-customer-card strong {
  display: block;
  margin-top: 7px;
  font-size: 0.95rem;
  line-height: 1.25;
}

.receipt-items {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 14px 16px;
  border-radius: 20px;
  background: #fffdf8;
  border: 1px solid rgba(124, 74, 43, 0.08);
  overflow: hidden;
}

.receipt-items-head {
  margin-bottom: 8px;
  font-weight: 700;
  font-size: 0.95rem;
}

.receipt-row {
  padding: 10px 0;
  border-bottom: 1px solid rgba(45, 31, 22, 0.08);
}

.receipt-row:last-child {
  border-bottom: 0;
}

.receipt-row > :nth-child(2),
.receipt-row > :nth-child(3) {
  flex-shrink: 0;
}

.receipt-row-head {
  padding-top: 0;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgba(45, 31, 22, 0.52);
  font-weight: 700;
}

.receipt-product {
  min-width: 0;
}

.receipt-product strong {
  display: block;
  margin-bottom: 2px;
  font-size: 0.96rem;
  line-height: 1.3;
}

.receipt-product small {
  display: block;
  color: rgba(45, 31, 22, 0.56);
  line-height: 1.3;
  font-size: 0.79rem;
}

.receipt-variant {
  margin-top: 4px;
  color: rgba(45, 31, 22, 0.68);
  font-weight: 600;
}

.receipt-promo {
  display: block;
  margin-top: 4px;
  color: var(--coffee-brown);
  font-weight: 700;
}

.receipt-empty-state {
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 14px;
  align-items: flex-start;
  justify-content: center;
  padding: 4px 2px;
}

.receipt-empty-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
}

.receipt-totals {
  margin-top: 16px;
  padding: 18px 20px;
  border-radius: 20px;
  background: #2d1f16;
  color: #fff4e9;
}

.receipt-totals.is-empty {
  width: 100%;
  margin-top: 4px;
}

.total-row {
  padding: 8px 0;
  font-size: 0.95rem;
}

.total-row.is-negative strong {
  color: #ffc0a8;
}

.total-row.is-grand {
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.total-row.is-grand strong {
  font-size: 1.35rem;
}

.payment-shell,
.promo-shell,
.promo-live-wrap,
.promo-image-wrap {
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 14px;
}

.promo-shell h3,
.payment-shell h3 {
  margin: 4px 0;
  font-size: clamp(1.35rem, 1.8vw, 1.9rem);
  line-height: 1.12;
  color: var(--coffee-ink);
}

.qr-card {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  border-radius: 20px;
  background: #fffaf4;
  min-height: 220px;
  border: 1px solid rgba(124, 74, 43, 0.08);
}

.payment-amount,
.promo-banner {
  padding: 16px 18px;
  border-radius: 18px;
  background: #fff6ea;
  color: var(--coffee-ink);
  border: 1px solid rgba(124, 74, 43, 0.08);
}

.payment-amount strong,
.promo-banner strong {
  font-size: 1.45rem;
  color: var(--coffee-brown);
}

.payment-amount span,
.promo-banner span {
  color: var(--coffee-muted);
}

.promo-banner-live {
  background: linear-gradient(135deg, rgba(255, 235, 203, 0.92), rgba(235, 251, 245, 0.92));
}

.promo-image-wrap {
  gap: 12px;
}

.promo-image-stage {
  position: relative;
  flex: 1;
  min-height: 330px;
  overflow: hidden;
  border-radius: 22px;
  background: #1f2937;
  border: 1px solid rgba(124, 74, 43, 0.08);
  box-shadow: 0 16px 32px rgba(111, 73, 48, 0.12);
}

.promo-image-stage img {
  width: 100%;
  height: 100%;
  min-height: 330px;
  object-fit: cover;
  display: block;
}

.promo-image-fallback {
  display: grid;
  height: 100%;
  min-height: 330px;
  place-items: center;
  color: rgba(255, 255, 255, 0.74);
  font-weight: 800;
}

.promo-caption-panel {
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 18px;
  padding: 16px 18px;
  border-radius: 18px;
  background: rgba(45, 31, 22, 0.78);
  color: #fff8ef;
  backdrop-filter: blur(10px);
}

.promo-caption-panel .section-kicker {
  color: rgba(255, 232, 199, 0.86);
}

.promo-caption-panel h3 {
  margin: 7px 0 0;
  color: #ffffff;
  font-size: clamp(1.12rem, 1.55vw, 1.5rem);
  line-height: 1.32;
}

.payment-note {
  padding: 16px 18px;
  border-radius: 18px;
  background: #eefbf7;
  color: rgba(45, 31, 22, 0.82);
  border: 1px solid rgba(120, 213, 196, 0.24);
}

.promo-live-meta,
.promo-list {
  display: grid;
  gap: 12px;
}

.promo-live-meta {
  flex: 1;
}

.promo-list {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.promo-live-card,
.promo-list-card {
  padding: 16px 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(124, 74, 43, 0.08);
  box-shadow: 0 12px 26px rgba(111, 73, 48, 0.08);
}

.promo-list-card {
  min-width: 0;
  padding: 8px;
}

.promo-list-card img {
  width: 100%;
  aspect-ratio: 1.25;
  object-fit: cover;
  display: block;
  border-radius: 12px;
  margin-bottom: 9px;
}

.promo-live-card span,
.promo-list-card span {
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 0.68rem;
  font-weight: 700;
  color: rgba(45, 31, 22, 0.56);
}

.promo-live-card strong,
.promo-list-card strong {
  display: block;
  font-size: 1rem;
  color: var(--coffee-ink);
}

.promo-list-card strong {
  min-height: 2.7em;
  font-size: 0.82rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.promo-live-card strong {
  margin-top: 8px;
  line-height: 1.35;
}

.promo-list-card p {
  margin-top: 8px;
  -webkit-line-clamp: 3;
}

@media (max-width: 1199px) {
  .customer-display-viewport {
    padding: 16px;
  }

  .customer-display-header,
  .transaction-stage {
    display: grid;
    grid-template-columns: 1fr;
  }

  .header-meta {
    min-width: 0;
  }
}

@media (max-width: 767px) {
  .customer-display-viewport {
    padding: 14px;
    gap: 14px;
  }

  .header-meta,
  .receipt-customer,
  .receipt-empty-grid {
    grid-template-columns: 1fr;
  }

  .brand-wrap {
    align-items: flex-start;
  }

  .receipt-head,
  .receipt-items-head,
  .receipt-row,
  .total-row,
  .payment-amount,
  .promo-banner {
    flex-direction: column;
    align-items: flex-start;
  }

  .receipt-row {
    gap: 6px;
  }

  .promo-list {
    grid-template-columns: 1fr;
  }

  .promo-image-stage,
  .promo-image-stage img,
  .promo-image-fallback {
    min-height: 280px;
  }
}

@media (max-height: 900px) {
  .customer-display-viewport {
    padding: 14px;
    gap: 12px;
  }

  .brand-mark {
    width: 60px;
    height: 60px;
    border-radius: 18px;
  }

  .brand-copy p,
  .meta-card small {
    display: none;
  }

  .meta-card,
  .receipt-shell,
  .experience-shell {
    padding: 16px;
  }

  .receipt-items,
  .receipt-totals,
  .payment-amount,
  .promo-banner,
  .payment-note,
  .promo-live-card,
  .promo-list-card {
    padding: 14px 16px;
  }

  .receipt-customer-card {
    padding: 12px 14px;
  }

  .qr-card {
    min-height: 190px;
    padding: 16px;
  }

  .promo-image-stage,
  .promo-image-stage img,
  .promo-image-fallback {
    min-height: 260px;
  }
}

@media (max-height: 760px) {
  .brand-copy h1 {
    font-size: 1.55rem;
  }

  .customer-display-header {
    gap: 12px;
  }

  .header-meta {
    gap: 8px;
  }

  .meta-card strong {
    font-size: 1rem;
  }

  .receipt-head h2 {
    font-size: 1.25rem;
  }

  .promo-shell p,
  .payment-shell p,
  .receipt-empty-state p,
  .promo-list-card p {
    -webkit-line-clamp: 2;
  }
}
</style>
